import { EventSeverity } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import SpeedIcon from '@cobliteam/ui-business/icons/filters/speed.svg?react';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectMediaSeverityProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectMediaSeverity = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectMediaSeverityProps) => {
  const { t } = useTranslation('multiselects');

  const eventSeverityLabels: { [key in EventSeverity]: string } = {
    [EventSeverity.MEDIUM]: t('media_severity.medium'),
    [EventSeverity.HIGH]: t('media_severity.high'),
    [EventSeverity.HIGHER]: t('media_severity.higher'),
    [EventSeverity.LOW]: t('media_severity.low'),
  };

  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);

  // check if all selected types are in draft
  const hasSameElements = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = draft.find((d) => d === type);
    const updatedSelected = isSelected ? draft.filter((d) => d !== type) : [...draft, type];
    setDraft(updatedSelected as EventSeverity[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('media_severity.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={Object.values(EventSeverity)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => onChangeSelect(item)} />
                  <Label htmlFor={item}>{eventSeverityLabels[item]}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('media_severity.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('media_severity.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<SpeedIcon />}>
        {t('media_severity.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectMediaSeverity;
